/**
 * Vue Router setup for the dashboard SPA.
 *
 * Top-level routes live in `./controllers` — one Vue component per
 * primary nav destination (Home, Insights, Sources, Settings, Docs,
 * AI, Login). The detail surfaces for an individual insight (view,
 * version history, diff) continue to live in `./views` and are
 * mounted underneath `/insights/:id`.
 */

import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { canEdit, loadCurrentUser } from "./auth.ts";

const routes: RouteRecordRaw[] = [
  {
    // Lean-version redirect: the standalone Home dashboard is hidden
    // along with the rest of the non-insights nav, so `/` punts
    // straight to the insights list. The Home component is left
    // imported in the codebase so restoring the route is a one-line
    // revert once the full nav comes back.
    path: "/",
    name: "home",
    redirect: { name: "list" },
  },
  {
    // Development entry point for the Home dashboard while `/` is
    // still in lean-redirect mode. Lets contributors preview the
    // landing page before it's promoted back to `/`.
    path: "/home",
    name: "home-preview",
    component: () => import("./controllers/Home.vue"),
  },
  {
    path: "/login",
    name: "login",
    component: () => import("./controllers/Login.vue"),
  },
  {
    path: "/insights",
    name: "list",
    component: () => import("./controllers/Insights.vue"),
  },
  {
    path: "/views",
    name: "views",
    component: () => import("./views/ViewsList.vue"),
  },
  {
    path: "/insights/:id",
    name: "view",
    component: () => import("./views/InsightView.vue"),
    props: true,
  },
  {
    path: "/insights/:id/versions",
    name: "versions",
    component: () => import("./views/VersionHistory.vue"),
    props: true,
  },
  {
    path: "/insights/:id/diff",
    name: "diff",
    component: () => import("./views/DiffView.vue"),
    props: (route) => ({
      id: route.params.id,
      from: Number(route.query.from ?? 1),
      to: Number(route.query.to ?? 2),
    }),
  },
  {
    path: "/dashboards",
    name: "dashboards",
    component: () => import("./controllers/Dashboards.vue"),
  },
  {
    path: "/sources",
    name: "sources",
    component: () => import("./controllers/Sources.vue"),
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("./controllers/Settings.vue"),
  },
  {
    path: "/docs",
    name: "docs",
    component: () => import("./controllers/Docs.vue"),
  },
  {
    // Data Explorer tab. Nested layout (`children`) so future modules
    // — query inspector / lineage (#15), full schema browser (#12) —
    // become sibling routes without re-shaping the tab.
    path: "/data",
    component: () => import("./controllers/DataExplorer.vue"),
    children: [
      { path: "", redirect: { name: "data.sql" } },
      {
        path: "sql",
        name: "data.sql",
        component: () => import("./views/data-explorer/SqlExplorer.vue"),
      },
    ],
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Auth guard: every navigation checks the session via insights-api's
// `/api/tenant/auth/me` (cached after the first call). Unauthenticated users
// targeting any route other than `/login` get bounced to `/login` with
// `return_to` set to the path they tried to reach. An already-authenticated
// user hitting `/login` is sent home so they don't see the sign-in form for
// no reason.
//
// `/login` stays a local SPA route rather than redirecting straight to the IdP:
// it gives an unauthenticated visitor a page instead of an immediate off-site
// bounce, and it's the one route that must remain reachable while signed out.
// It hands off via `startLogin()`, which is what actually leaves for
// insights-api.
router.beforeEach(async (to) => {
  const me = await loadCurrentUser();
  if (to.name === "login") {
    return me ? { path: "/" } : true;
  }
  if (!me) {
    return { name: "login", query: { return_to: to.fullPath } };
  }
  // Data Explorer is editor-only: insights-api 403s both its query and
  // its schema routes for the `user` tier. Bouncing here rather than
  // letting the view mount means a read-only user sees the insights
  // list instead of an explorer that errors on every action.
  //
  // This is a UX redirect, not a security control — the API is the
  // gate. `canEdit` is fail-closed, so a failed role lookup redirects
  // rather than admitting.
  if (to.path.startsWith("/data") && !canEdit.value) {
    return { path: "/insights" };
  }
  return true;
});
